/**
 * PortalFAB - Floating action button for the portal.
 *
 * Renders a single round Guru Cloud AI button in the bottom-right that opens
 * the Guru feedback widget (feature requests / bug reports). The widget's
 * native button stays hidden via a persistent CSS rule; clicking the FAB
 * programmatically clicks the hidden Guru button to open the overlay.
 *
 * The widget script loads at most once per page lifetime and only after the
 * authenticated user is known — see guruWidgetLoader.ts for why (the widget
 * freezes its notification identity at init and cannot be re-registered).
 * The FAB is not rendered when the widget isn't active for the current user.
 */

import { useRef, useEffect, useCallback, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { syncGuruWidgetMetadata, clearGuruWidgetMetadata } from '../../utils/guruWidgetMetadata';
import {
  ensureGuruWidget,
  setGuruNativeButtonHidden,
  subscribeGuruWidget,
  getGuruWidgetBoundUserId,
} from '../../utils/guruWidgetLoader';
import FABTooltipReminder from './FABTooltipReminder';
import styles from './PortalFAB.module.css';

/** Open the Guru widget overlay by clicking its hidden button. */
function openGuruWidget(): void {
  const btn = document.getElementById('guru-widget-btn');
  if (btn) {
    btn.click();
  }
}

const PortalFAB: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  // The widget follows the real person at the keyboard: while an admin uses
  // "View as", that is the admin (so the widget stays up on every page), and
  // the viewed account rides along as viewed_as_* metadata.
  const { user, realUser } = useAuth();
  const location = useLocation();
  const userId = realUser?.id ?? null;
  const viewedAs = realUser && user && user.id !== realUser.id ? user : null;
  const boundUserId = useSyncExternalStore(subscribeGuruWidget, getGuruWidgetBoundUserId);
  const widgetActive = boundUserId !== null && boundUserId === userId;

  // Declared before the loader effect so metadata exists before the script.
  useEffect(() => {
    syncGuruWidgetMetadata(realUser, 'portal', location.pathname, location.search, viewedAs);
  }, [realUser, viewedAs, location.pathname, location.search]);

  // Don't leave identity metadata behind on pages without a widget embed.
  useEffect(() => {
    return () => {
      clearGuruWidgetMetadata();
    };
  }, []);

  useEffect(() => {
    // The portal always drives the widget through the themed FAB, so the
    // native launcher stays hidden regardless of widget state.
    setGuruNativeButtonHidden(true);
    ensureGuruWidget(userId, {
      color: '#0B4FA3',
      title: 'Request a Feature or Report a Bug',
    });
  }, [userId]);

  const handleFabClick = useCallback(() => {
    openGuruWidget();
  }, []);

  if (!widgetActive) return null;

  return (
    <div ref={containerRef}>
      <FABTooltipReminder />
      <button
        className={styles.fab}
        onClick={handleFabClick}
        title="Request a Feature or Report a Bug"
        aria-label="Request a feature or report a bug"
      >
        <img
          src="/img/gurucloudai-logo.png"
          alt="Guru Cloud AI"
          className={styles.fabIcon}
          draggable={false}
        />
      </button>
    </div>
  );
};

export default PortalFAB;
